import * as y from "yup";

const mediaSchema = y.object({
  portrait: y
    .array()
    .of(
      y.object({
        hash: y.string().nullable(),
        picture_url: y.string().nullable(),
        fileKey: y.string().nullable(),
        name: y.string().nullable(),
        description: y.string().nullable(),
        type: y.string().nullable(),
      })
    )
    .default([]),
  square: y
    .array()
    .of(
      y.object({
        hash: y.string().nullable(),
        picture_url: y.string().nullable(),
        fileKey: y.string().nullable(),
        name: y.string().nullable(),
        description: y.string().nullable(),
        type: y.string().nullable(),
      })
    )
    .default([]),
});

const videoSchema = y.object({
  portrait: y
    .array()
    .of(
      y.object({
        id: y.string().nullable(),
        external_id: y.string().nullable(),
        thumbnail_url: y.string().nullable(),
        source: y.string().nullable(),
        fileKey: y.string().nullable(),
        type: y.string().nullable(),
      })
    )
    .default([]),
  square: y
    .array()
    .of(
      y.object({
        id: y.string().nullable(),
        external_id: y.string().nullable(),
        thumbnail_url: y.string().nullable(),
        source: y.string().nullable(),
        fileKey: y.string().nullable(),
        type: y.string().nullable(),
      })
    )
    .default([]),
});

export const AdCreativeFormSchema = y.object({
  id: y.string().nullable(),
  name: y.string().required("Please provide a name for the creative"),
  external_id: y.string().nullable(),
  adset_id: y.string().nullable(),
  organization_id: y.string(),
  media_type: y
    .string()
    .oneOf(["single", "carousel"], "Media type must be single or carousel"),
  carousel_media_type: y
    .string()
    .nullable()
    .oneOf(["photo", "video"], "Carousel media type must be photo or video"),
  ad_formats: y.array().of(y.string()).required("Please select an ad format"),
  message: y
    .string()
    .required("Please provide a message for the creative")
    .min(3, "Message must be at least 3 characters"),
  status: y.string().nullable(),
  call_to_action: y.object({
    value: y.string().nullable(),
    label: y.string().nullable(),
  }),
  campaign_id: y.string().required("Campaign ID is required"),
  website_url: y.string().nullable(),
  facebook_placements: y.array().of(y.string()).default([]),
  instagram_placements: y.array().of(y.string()).default([]),
  page_id: y
    .object({
      value: y.string().required("Please select a Facebook page"),
      label: y.string().required("Page label is required"),
    })
    .required("Please select a Facebook page")
    .nullable(),
  instagram_actor_id: y
    .object({
      value: y.string().required("Please select an Instagram account"),
      label: y.string().required("Instagram account label is required"),
    })
    .required("Please select an Instagram account")
    .nullable(),
  images: mediaSchema.test({
    name: "images-validation",
    message: "Invalid images configuration",
    test: function (images) {
      const { parent } = this;
      const mediaType = parent.media_type;
      const carouselMediaType = parent.carousel_media_type;

      // For single ads with images, require both portrait and square
      if (mediaType === "single") {
        const hasPortrait =
          images?.portrait?.length > 0 && images.portrait[0]?.picture_url;
        const hasSquare =
          images?.square?.length > 0 && images.square[0]?.picture_url;

        // Only validate if images exist (user might be using videos instead)
        if (hasPortrait || hasSquare) {
          if (!hasPortrait || !hasSquare) {
            return this.createError({
              path: "images",
              message: "Image ads require both portrait and square images",
            });
          }
        }
        return true;
      }

      // For carousel with photos, require 2-10 images in portrait format only
      if (mediaType === "carousel" && carouselMediaType === "photo") {
        const portraitCount = images?.portrait?.length || 0;

        if (portraitCount < 2 || portraitCount > 10) {
          return this.createError({
            path: "images.portrait",
            message: `Carousel photos must have between 2 and 10 items (currently has ${portraitCount})`,
          });
        }
        return true;
      }

      return true;
    },
  }),
  videos: videoSchema.test({
    name: "videos-validation",
    message: "Invalid videos configuration",
    test: function (videos) {
      const { parent } = this;
      const mediaType = parent.media_type;
      const carouselMediaType = parent.carousel_media_type;

      // For single ads with videos, require both portrait and square
      if (mediaType === "single") {
        const hasPortrait =
          videos?.portrait?.length > 0 && videos.portrait[0]?.source;
        const hasSquare =
          videos?.square?.length > 0 && videos.square[0]?.source;

        // Only validate if videos exist (user might be using images instead)
        if (hasPortrait || hasSquare) {
          if (!hasPortrait || !hasSquare) {
            return this.createError({
              path: "videos",
              message: "Video ads require both portrait and square videos",
            });
          }
        }
        return true;
      }

      // For carousel with videos, require 2-10 videos in portrait format only
      if (mediaType === "carousel" && carouselMediaType === "video") {
        const portraitCount = videos?.portrait?.length || 0;

        if (portraitCount < 2 || portraitCount > 10) {
          return this.createError({
            path: "videos.portrait",
            message: `Carousel videos must have between 2 and 10 items (currently has ${portraitCount})`,
          });
        }
        return true;
      }

      return true;
    },
  }),
});

// Type for the form data that matches the schema
export type AdCreativeFormData = y.InferType<typeof AdCreativeFormSchema>;
